import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiX } from 'react-icons/fi';
import { useLiveChallenge } from '@/hooks/useLiveChallenge';

/**
 * AnswerFeedback - Mostra se o aluno acertou + pontos + ranking.
 * Auto-navega para QuestionPage quando nova questão é revelada,
 * ou para FinalResultPage quando a sessão termina.
 */
const AnswerFeedback = () => {
  const navigate = useNavigate();
  const { session, lastAnswerFeedback, myScore, currentQuestion, sessionStatus } = useLiveChallenge();

  const prevQuestionIdRef = useRef(currentQuestion?.id);

  useEffect(() => {
    if (currentQuestion?.id && currentQuestion.id !== prevQuestionIdRef.current) {
      navigate(`/desafio-ao-vivo/${session?.live_challenge_id}/questao`, { replace: true });
    }
  }, [currentQuestion?.id, navigate, session?.live_challenge_id]);

  useEffect(() => {
    if (sessionStatus === 'finished') {
      navigate(`/desafio-ao-vivo/${session?.live_challenge_id}/final`, { replace: true });
    }
  }, [sessionStatus, navigate, session?.live_challenge_id]);

  const isCorrect = lastAnswerFeedback?.is_correct ?? lastAnswerFeedback?.correct ?? false;
  const pointsEarned = lastAnswerFeedback?.points_earned ?? lastAnswerFeedback?.['points-earned'] ?? 0;
  const currentRank = myScore?.rank ?? myScore?.position ?? null;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-bright px-4 py-12">
      <div className="flex w-full max-w-md flex-col items-center gap-6 rounded-2xl bg-white px-8 py-10 text-center shadow-dropshadow-lg">
        <div className={`flex h-24 w-24 animate-[scale-in_0.4s_ease-out] items-center justify-center rounded-full text-white shadow-dropshadow-md ${isCorrect ? 'bg-success' : 'bg-danger'}`}>
          {isCorrect ? (
            <FiCheck
              className="h-12 w-12"
              strokeWidth={3}
            />
          ) : (
            <FiX
              className="h-12 w-12"
              strokeWidth={3}
            />
          )}
        </div>

        <h1 className={`m-0 text-3xl font-extrabold ${isCorrect ? 'text-success' : 'text-danger'}`}>{isCorrect ? 'Correto!' : 'Incorreto'}</h1>

        {pointsEarned > 0 && (
          <div className={`flex flex-col items-center gap-1 rounded-xl px-8 py-4 ${isCorrect ? 'bg-success-lightest' : 'bg-bright'}`}>
            <span className={`text-3xl font-extrabold ${isCorrect ? 'text-success' : 'text-neutral-dark'}`}>+{pointsEarned}</span>
            <span className="text-sm font-semibold text-neutral">pontos</span>
          </div>
        )}

        {currentRank && (
          <div className="flex flex-col items-center gap-1 rounded-xl bg-primary-lightest px-8 py-4">
            <span className="text-3xl font-extrabold text-primary-dark">{currentRank}º</span>
            <span className="text-sm font-semibold text-neutral">lugar</span>
          </div>
        )}

        <div className="mt-2 flex flex-col items-center gap-3">
          <div className="flex gap-2">
            <span className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary [animation-delay:0ms]" />
            <span className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary [animation-delay:150ms]" />
            <span className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary [animation-delay:300ms]" />
          </div>
          <p className="m-0 text-sm font-medium text-neutral">Aguardando próxima questão...</p>
        </div>
      </div>

      <style>{`
        @keyframes scale-in {
          0% { transform: scale(0.3); opacity: 0; }
          60% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default AnswerFeedback;
